import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CollectPage, CollectPagePatch } from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

const COLLECT_PAGE_KEY = "collectPage" as const;

/** Absolute URL a recipient opens to fill in the public collect form. */
export function shareUrl(token: string): string {
  return `${window.location.origin}/share/${token}`;
}

/** The account's singleton Collect page (auto-provisioned on first read). */
export function useCollectPage() {
  return useQuery<CollectPage, ApiClientError>({
    queryKey: [COLLECT_PAGE_KEY],
    queryFn: () => apiFetch<CollectPage>("/collect-page"),
  });
}

/** Publish/unpublish and/or set, change, or clear (`accessCode: null`) the code. */
export function useUpdateCollectPage() {
  const qc = useQueryClient();
  return useMutation<CollectPage, ApiClientError, CollectPagePatch>({
    mutationFn: (body) =>
      apiFetch<CollectPage>("/collect-page", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (page) => {
      qc.setQueryData([COLLECT_PAGE_KEY], page);
    },
  });
}

/** Rotate the public link. The old URL stops working immediately. */
export function useResetCollectPageLink() {
  const qc = useQueryClient();
  return useMutation<CollectPage, ApiClientError, void>({
    mutationFn: () => apiFetch<CollectPage>("/collect-page/reset-link", { method: "POST" }),
    onSuccess: (page) => {
      qc.setQueryData([COLLECT_PAGE_KEY], page);
    },
  });
}

// --- Client-side sharing helpers (no server round-trip) ---

export interface ShareTargetOptions {
  requesterName?: string | null;
  accessCode?: string | null;
}

export interface ShareTargets {
  message: string;
  sms: string;
  email: string;
  whatsapp: string;
  /** Best-effort mobile deeplink; web Messenger sharing needs an FB app ID. */
  messenger: string;
}

const SHARE_SUBJECT = "Share your contact details";

/** The prefilled message body used across every share target. */
export function buildShareMessage(url: string, opts: ShareTargetOptions = {}): string {
  const intro = opts.requesterName
    ? `${opts.requesterName} would like your contact details.`
    : "Please share your contact details.";
  const codeLine = opts.accessCode ? `\nAccess code: ${opts.accessCode}` : "";
  return `${intro}\n${url}${codeLine}`;
}

/** Build per-app deeplink URLs that prefill the share message. */
export function buildShareTargets(url: string, opts: ShareTargetOptions = {}): ShareTargets {
  const message = buildShareMessage(url, opts);
  const enc = encodeURIComponent(message);
  return {
    message,
    sms: `sms:?&body=${enc}`,
    email: `mailto:?subject=${encodeURIComponent(SHARE_SUBJECT)}&body=${enc}`,
    whatsapp: `https://wa.me/?text=${enc}`,
    messenger: `fb-messenger://share?link=${encodeURIComponent(url)}`,
  };
}

export function canNativeShare(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

/** Open the OS share sheet (mobile). No-op / silent on cancel or when unsupported. */
export async function nativeShare(url: string, opts: ShareTargetOptions = {}): Promise<void> {
  if (!canNativeShare()) return;
  try {
    await navigator.share({
      title: SHARE_SUBJECT,
      text: buildShareMessage(url, opts),
      url,
    });
  } catch {
    // User dismissed the sheet, or the share failed — nothing to do.
  }
}
